import React, { useState } from 'react';
import { Button, Divider, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PaperCard } from 'components/Card';
import { EXTERNAL, paths } from 'routes';
import { FormProvider, TextField } from 'components/Form';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { loginSchema } from '../validation';
import { useAuth } from 'lib/auth';
import InputAdornment from '@mui/material/InputAdornment';
import { IconWrapper } from 'components/Icon';
import { YellowMessageBar } from 'components/YellowMessageBar';
import { RedMessageBar } from 'components/RedMessageBar';
import { Loader } from 'components/Loader';
import { membershipIdForVat } from '../api/register';

export const LoginForm = ({
  onSuccess,
  values,
  registrationFlow = false,
  existingUser = false,
  msg = '',
  acceptInvitation = false,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState({ companyEmail: values?.companyEmail ?? '', password: '' });
  const { login, register, refreshSession } = useAuth();
  const [redMsg, setRedMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(
    msg
      ? msg
      : !registrationFlow
      ? ''
      : existingUser
      ? t('website:auth.login.messages.existingUser')
      : t('website:auth.login.messages.newUser')
  );

  const methods = useForm({
    mode: 'onSubmit',
    resolver: useYupValidationResolver(loginSchema),
    values: data,
  });
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      methods.setError(null);
      setMessage('');
      setLoading(true);
      const response = await login(data);
      if (!response.success && response.requiresVerification) {
        const stateData = {companyEmail: data.companyEmail, initLoad: false, fullLayout: true};
        navigate(paths.auth.confirmationSent, { state: {stateData} });
        return;
      }

      if (registrationFlow && !existingUser) {
        const registered = await register(values, false);
        await refreshSession(membershipIdForVat(registered?.data, values.vat));
      }

      await onSuccess(response);
    } catch (error) {
      setLoading(false);
      setRedMsg(error.message);
      methods.setError('password', { type: 'custom', message: error.message });
    }
  };

  const iconAdornment = registrationFlow
    ? {
        endAdornment: (
          <InputAdornment position="end">
            <IconWrapper
              icon="lockClosed"
              sx={{
                svg: {
                  width: 16,
                  height: 16,
                },
              }}
            />
          </InputAdornment>
        ),
      }
    : {};

  const emailValue = registrationFlow ? values.companyEmail : data.companyEmail;
  //const message = existingUser ? "We have detected that the e-mail you entered is already in use, and that you have access to the company. Please log in." : "We have identified that your e-mail already in use, please log in to proceed with registration.";
  const titleMargin = registrationFlow || redMsg ? 0.5 : 3;

  return (
    <PaperCard px={{ xs: 2, sm: 4 }} width={{ xs: 1, sm: 368 }} maxWidth={368} sx={{my:5}}>
      <Stack >
        <Typography variant="h5" sx={{ mb: titleMargin, textAlign: "center" }}>
          {t('website:auth.login.heading')}
        </Typography>
        {message && <YellowMessageBar message={message} />}
        {redMsg && <RedMessageBar message={redMsg} />}

        <FormProvider methods={methods} onSubmit={onSubmit}>
          <Stack>
            <TextField
              name="companyEmail"
              label={t('website:auth.login.fields.companyEmail.label')}
              placeholder={t('website:auth.login.fields.companyEmail.placeholder')}
              required={true}
              type="email"
              autoComplete="username"
              value={emailValue}
              onChange={(e) => setData({ ...data, companyEmail: e.target.value })}
              sx={{ mb: 2 }}
              fullWidth
              disabled={registrationFlow}
              InputProps={iconAdornment}
            />
            <TextField
              name="password"
              label={t('website:auth.login.fields.password.label')}
              placeholder={t('website:auth.login.fields.password.placeholder')}
              required={true}
              type="password"
              autoComplete="current-password"
              value={data.password}
              onChange={(e) => setData({ ...data, password: e.target.value })}
              error={Boolean(methods?.formState?.errors?.password)}
              helperText={methods?.formState?.errors?.password?.message}
              sx={{ mb: 0.5 }}
              fullWidth
            />

            <Typography
              component={Link}
              to={paths.auth.forgotPassword}
              variant="caption"
              fontWeight="bold"
              sx={({ palette }) => ({
                color: `${palette.primary.main} !important`,
              })}
            >
              {t('website:auth.login.forgotPasswordLink')}
            </Typography>

            <Button
              size="large"
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading ? true : false}
              sx={{
                mt: 2,
                mb: 0.5,
                bgcolor: "primary.light",
                '&:hover': {
                  backgroundColor: "primary.light",
                },
              }}
            >
              {loading ?
              (
                <Loader style={{width: "auto", height: "40px", marginTop: "-2.5%"}}/>
              )
              :
              (
                 <>{t('website:auth.login.submitButton')}</>
              )
              }
            </Button>

            <Stack
              align="center"
              gap={1.5}
              sx={{
                a: {
                  color: 'inherit',
                  fontWeight: 700,
                  textTransform: 'none',
                },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 400, color: "text.primary" }}>
                {t('website:auth.login.termsAgreement.before')}{' '}
                <a href={paths.website.termsAndConditions}  target='_blank' rel="noreferrer">{t('website:auth.login.termsAgreement.linkText')}</a>.
                {/* {' and '} */}
                {/* <a href={EXTERNAL.PRIVACY_POLICE}  target='_blank' rel="noreferrer">Privacy Policy</a> */}
              </Typography>

              <Divider sx={{ width: 1 }} />

              <Typography variant="caption" sx={{ fontWeight: 400, color: "text.primary" }}>
                {t('website:auth.login.noAccountTextBefore')} <Link to={paths.auth.register}>{t('website:auth.login.noAccountLinkText')}</Link>
              </Typography>
            </Stack>
          </Stack>
        </FormProvider>
      </Stack>
    </PaperCard>
  );
};
